import React, { useMemo, useState, useEffect, useContext } from "react";
import { useMaterialReactTable, MaterialReactTable } from "material-react-table";
import { Box, InputLabel, Typography, CircularProgress } from "@mui/material";
import { Input, OVERFLOW } from "abs-ui-components";
import { MDBRow, MDBCol } from "mdbreact";
import "./FEUMRVFEUContribution.scss";
import { useSelector } from "react-redux";
import { FEU_CHILD_API_ROUTES } from "../../FEUChildApiRoutes";
import { callApi } from "../../../../../../services/MonitoringPlanService";
import { roundOff } from "../../../../../../services/utilities";
import { FeatureType } from "../../../../../../constants/FeatureToggles";
import { ClaimsContext } from "../../../../../../services/ClaimsService";

const safeRound = (value, decimals = 3) => {
	if (value === null || value === undefined || value === "") return "";
	const num = Number(value);
	if (isNaN(num)) return "";
	return roundOff(num, decimals, decimals);
};

const FEUMRVFEUContribution = (props) => {

	const isUserInputEnabled = props?.isReportEnabled;
	const [validationErrors, setValidationErrors] = useState({});
	const reportDetails = useSelector((state) => state.reports.selectedReportDetails);
	const [feuData, setFeuData] = useState({});
	const { fuelData = [], energySummary = {}, iceClass = {} } = feuData;
	const [isLoading, setLoading] = useState(false);
	const { userHasFeature } = useContext(ClaimsContext);

	const fetchFeuContribution = async () => {
		try {
			setLoading(true);
			const response = await callApi({
				method: "GET",
				url: FEU_CHILD_API_ROUTES.GET_ENERGY_CONTRIBUTION(reportDetails.ReportMstId),
				data: {},
			});
			setLoading(false);
			const data = Array.isArray(response) ? response[0] : response;

			const normalizedFuelData = (data.fuelData || []).map((item) => ({
				...item,
				allocatedEiIce: item.allocatedEiIce !== null && item.allocatedEiIce !== undefined ? safeRound(item.allocatedEiIce) : "",
				feuEnergyContribution: item.feuEnergyContribution !== null && item.feuEnergyContribution !== undefined ? safeRound(item.feuEnergyContribution) : "",
			}));

			setFeuData({
				fuelData: normalizedFuelData,
				energySummary: data.energySummary || {},
				iceClass: data.iceClass || {},
			});
		} catch (error) {
			console.error("Error fetching FuelEU Contribution data", error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchFeuContribution();
	}, []);

	const [fuelDataState, setFuelDataState] = useState([]);
	useEffect(() => {
		setFuelDataState(fuelData);
	}, [fuelData]);

	const recalculateValues = async (updatedBackendData, editedRowIndex) => {
		try {
			const editedRow = fuelDataState[editedRowIndex];
			const rank = editedRow?.rank;

			const fullPayload = {
				reportMstId: reportDetails.ReportMstId,
				...updatedBackendData,
			};

			const response = await callApi({
				method: "POST",
				url: FEU_CHILD_API_ROUTES.CALCULATE_USER_VALUES(rank),
				data: JSON.stringify(fullPayload),
			});

			const result = await response;

			if (result.updatedBackendData) {
				setFeuData(result.updatedBackendData);
				setFuelDataState(result.updatedBackendData.fuelData);
			}

			await fetchFeuContribution();
			return result;
		} catch (error) {
			throw error;
		}
	};

	const columns1 = useMemo(
		() => [
			{ id: "fuelType", accessorKey: "fuelType", header: "Fuel Type",size:220 },
			{ id: "cSlip", accessorKey: "cSlip", header: "Cslip (%)", Cell: ({ cell }) => safeRound(cell.getValue()),size:100 },
			{ id: "activity", accessorKey: "activity", header: "Activity",size:120 },
			{ id: "criteria", accessorKey: "criteria", header: "Differentiating Criteria",size:150 },
			{ id: "lcvEu", accessorKey: "lcvEu", accessorKey: "lcvEu", header: "LCV EU (MJ/g)", Cell: ({ cell }) => safeRound(cell.getValue()) },
			{ id: "totalCons", accessorKey: "totalCons", header: "Total CONS (MT)", Cell: ({ cell }) => safeRound(cell.getValue()) },
			{ id: "wtwCO2eqEmissions", accessorKey: "wtwCO2eqEmissions", header: "WtW CO2eq emissions (MT)", Cell: ({ cell }) => safeRound(cell.getValue()) },
			{ id: "wtwGHGIntensity", accessorKey: "wtwGHGIntensity", header: "WtW GHG Intensity (gCO2eq/MJ)", Cell: ({ cell }) => safeRound(cell.getValue()) },
			{ id: "totalEnergy", accessorKey: "totalEnergy", header: "Total Energy (GJ)", Cell: ({ cell }) => safeRound(cell.getValue()) },
			{
				id: "feuEnergyContribution",
				accessorKey: "feuEnergyContribution",
				header: "FEU Energy Contribution (GJ)",
				Cell: ({ cell, row }) => {
					const rawValue = cell.getValue();
					const value = rawValue === null || rawValue === undefined ? "" : rawValue;
					const errorKey = `feuEnergy-${row.index}`;
					const hasError = validationErrors[errorKey];
					const totalEnergy = row.original.totalEnergy ? parseFloat(row.original.totalEnergy) : 0;
					return isUserInputEnabled && userHasFeature?.(FeatureType.EnableContributionUserput) ? (
						<Box>
							<Input
								type="text"
								inputProps={{ inputMode: "decimal" }}
								value={value}
								autoFocus={false}
								onChange={(e) => {
									const newValue = e?.target?.value ?? e ?? "";
									if (newValue === "" || /^-?\d*\.?\d*$/.test(newValue)) {
										const newData = fuelDataState.map((item, idx) =>
											idx === row.index ? { ...item, feuEnergyContribution: newValue } : item
										);
										setFuelDataState(newData);

										const numericValue = newValue === "" ? 0 : parseFloat(newValue);
										if (numericValue > totalEnergy) {
											setValidationErrors((prev) => ({
												...prev,
												[errorKey]: `Value cannot exceed ${totalEnergy}`,
											}));
										} else {
											setValidationErrors((prev) => {
												const newErrors = { ...prev };
												delete newErrors[errorKey];
												return newErrors;
											});
										}
									}
								}}
								onBlur={async (e) => {
									const finalValue = e?.target?.value ?? value;

									const numericValue = finalValue === "" ? 0 : parseFloat(finalValue);
									if (numericValue > totalEnergy) {
										setValidationErrors((prev) => ({
											...prev,
											[errorKey]: `Value cannot exceed ${totalEnergy}`,
										}));
										return;
									}

									setValidationErrors((prev) => {
										const newErrors = { ...prev };
										delete newErrors[errorKey];
										return newErrors;
									});
									const roundedValue = safeRound(finalValue);

									const newData = fuelDataState.map((item, idx) =>
										idx === row.index ? { ...item, feuEnergyContribution: roundedValue } : item
									);
									setFuelDataState(newData);

									const updatedBackendData = {
										...feuData,
										fuelData: newData,
									};

									try {
										await recalculateValues(updatedBackendData, row.index);
									} catch (error) {
										console.error("API error:", error);

										const newData = fuelDataState.map((item, idx) =>
											idx === row.index ? { ...item, feuEnergyContribution: value } : item
										);
										setFuelDataState(newData);
									}
								}}
								className="report-summmery-input"
								error={hasError}
							/>
							{hasError && (
								<Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5, fontSize: "0.75rem" }}>
									{validationErrors[errorKey]}
								</Typography>
							)}
						</Box>
					) : (
						safeRound(value)
					);
				},
			},
			{
				id: "feuConsContribution", accessorKey: "feuConsContribution", header: "FEU CONS Contribution (MT)", Cell: ({ cell }) => safeRound(cell.getValue())
			},
		],
		[isUserInputEnabled, fuelDataState, feuData, validationErrors]
	);

	// Second Table Columns
	const columns2 = useMemo(
		() => [
			...columns1,
			{ id: "tableDivider", accessorKey: "tableDivider", header: "",size:20},
			{ id: "totalConsIceScope", accessorKey: "totalConsIceScope", header: "Total CONS Through Ice in FEU scope (MT)", Cell: ({ cell }) => safeRound(cell.getValue()) },
			{ id: "eVoyagesTotal", accessorKey: "eVoyagesTotal", header: "E voyages, total (GJ)", Cell: ({ cell }) => safeRound(cell.getValue()) },
			{ id: "eVoyagesIce", accessorKey: "eVoyagesIce", header: "E voyages, ice conditions (GJ)", Cell: ({ cell }) => safeRound(cell.getValue()) },
			{ id: "eVoyagesOpen", accessorKey: "eVoyagesOpen", header: "E voyages, open water (GJ)", Cell: ({ cell }) => safeRound(cell.getValue()) },
			{
				id: "allocatedEiIce",
				accessorKey: "allocatedEiIce",
				header: "Allocated Ei, additional ice (GJ)",
				Cell: ({ cell, row }) => {
					const value = cell.getValue() ?? "";
					const errorKey = `AddIceEnergy-${row.index}`;
					const hasError = validationErrors[errorKey];
					const feuEnergy = row.original.feuEnergyContribution ? parseFloat(row.original.feuEnergyContribution) : 0;

					return isUserInputEnabled && userHasFeature?.(FeatureType.EnableContributionUserput) ? (
						<Box>
							<Input
								type="text"
								inputProps={{ inputMode: "decimal" }}
								value={value}
								autoFocus={false}
								onChange={(e) => {
									const newValue = e?.target?.value ?? e ?? "";
									if (newValue === "" || /^-?\d*\.?\d*$/.test(newValue)) {
										const newData = fuelDataState.map((item, idx) => (idx === row.index ? { ...item, allocatedEiIce: newValue } : item));
										setFuelDataState(newData);

										const numericValue = newValue === "" ? 0 : parseFloat(newValue);
										if (numericValue > feuEnergy) {
											setValidationErrors((prev) => ({
												...prev,
												[errorKey]: `Value cannot exceed ${feuEnergy}`,
											}));
										} else {
											setValidationErrors((prev) => {
												const newErrors = { ...prev };
												delete newErrors[errorKey];
												return newErrors;
											});
										}
									}
								}}
								onBlur={async (e) => {
									const finalValue = e?.target?.value ?? value;

									const numericValue = finalValue === "" ? 0 : parseFloat(finalValue);
									if (numericValue > feuEnergy) {
										setValidationErrors((prev) => ({
											...prev,
											[errorKey]: `Value cannot exceed ${feuEnergy}`,
										}));
										return;
									}

									setValidationErrors((prev) => {
										const newErrors = { ...prev };
										delete newErrors[errorKey];
										return newErrors;
									});
									const roundedValue = safeRound(finalValue);

									const newData = fuelDataState.map((item, idx) =>
										idx === row.index ? { ...item, feuEnergyContribution: roundedValue } : item
									);
									setFuelDataState(newData);

									const updatedBackendData = {
										...feuData,
										fuelData: newData,
									};

									try {
										await recalculateValues(updatedBackendData, row.index);
									} catch (error) {
										console.error("API error:", error);

										const newData = fuelDataState.map((item, idx) => (idx === row.index ? { ...item, allocatedEiIce: value } : item));
										setFuelDataState(newData);
									}
								}}
								className="report-summmery-input"
								error={hasError}
							/>
							{hasError && (
								<Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5, fontSize: "0.75rem" }}>
									{validationErrors[errorKey]}
								</Typography>
							)}
						</Box>
					) : (
						safeRound(value)
					);
				},
			},

			{ id: "feuConsAdjusted", accessorKey: "feuConsAdjusted", header: "FEU CONS Contribution, adjusted (MT)", Cell: ({ cell }) => safeRound(cell.getValue()), },
			{ id: "feuWtwCO2eqContribution", accessorKey: "feuWtwCO2eqContribution", header: "FEU WtW CO2eq emissions Contribution (MT)", Cell: ({ cell }) => safeRound(cell.getValue()) },
		],
		[isUserInputEnabled, fuelDataState, feuData, validationErrors]
	);

	// Shared Table	Styles
	const baseTableConfig = {
		enableStickyHeader: true,
		enableSorting: false,
		enableRowSelection: false,
		enableColumnPinning: true,
		enableTopToolbar: false,
		enableBottomToolbar: false,
		enableColumnActions: false,
		enableGlobalFilter: false,
		enableColumnFilters: false,
		enableEditing: false,
		enablePagination: false,
		enableDensityToggle: false,
		enableFullScreenToggle: false,
		enableColumnVisibility: false,
		state: {
			isLoading: isLoading,
		},
		initialState: {
			columnOrder: columns2.map((column) => column.accessorKey) || [],
			columnPinning: { left: ["fuelType","cSlip","activity","criteria"] },
		},
		localization: { noRecordsToDisplay: "" },
		muiTableContainerProps: {
			sx: {
			     maxHeight:"500px",
				
			}
		},
		muiTableHeadCellProps: ({ column }) => {
			return {
				sx: {
				color: "rgb(255 255 255)",
				backgroundColor: column.columnDef?.accessorKey === "tableDivider" ? "#fff" : "#042a4c",
				borderBottom: column.columnDef?.accessorKey === "tableDivider" && "none",
				borderRight: "1px solid lightgray",
				"&:last-child": { borderRight: "none" },
				height: 68,
				paddingTop: 19,
				textAlign: "center",
				justifyContent: "center",
				alignItems: "center",
				// minWidth: 240,
				textOverflow: "ellipsis",
				fontFamily: "Roboto, sans-serif",
			},
		}
		},
		muiTableBodyCellProps: ({ cell }) => {
			const value = cell.getValue();

			const isDecimal = !isNaN(parseFloat(value)) && isFinite(value);

			// const roundedValue = isDecimal ? parseFloat(value).toFixed(3) : value;
			return {
				sx: {
					borderRight: "1px solid lightgray",
					"&:last-child": { borderRight: "none" },
					textAlign: "center",
					mb: 2,
					fontFamily: "Roboto, sans-serif",
					borderBottom: cell.column.id === "tableDivider" ? "none" : "1px solid rgba(224, 224, 224, 1)",
					backgroundColor: cell.column.id === "tableDivider" ? "#fff" : "inherit",
				},
				title: isDecimal ? value.toString() : undefined, // Set original value as tooltip
				// children: isDecimal ? roundedValue : value
				// children:
				// 	value === null || value === undefined ? "" : value,
			};
		},

	};

	const table1 = useMaterialReactTable({ columns: columns1, data: fuelDataState || [], ...baseTableConfig});

	const table2 = useMaterialReactTable({
		columns: columns2,
		data: fuelDataState,
		...baseTableConfig,
	});

	const iceClassDetails = [
		{ label: "Ice Class", value: iceClass.class },
		{ label: "D total (Total distance in FEU scope)", value: iceClass.dTotal, unit: "nm" },
		{ label: "D ice conditions (Total distance through ICE in FEU scope)", value: iceClass.dIceConditions, unit: "nm" },
		{ label: "D open water (Total distance in open water FEU scope)", value: iceClass.dOpenWater, unit: "nm" },
		{ label: "E voyages, total", value: iceClass.eVoyagesTotal, unit: "GJ" },
		{ label: "E voyages, ice conditions", value: iceClass.eVoyagesIceConditions, unit: "GJ" },
		{ label: "E voyages, open water", value: iceClass.eVoyagesOpenWater, unit: "GJ" },
		{ label: "E/D open water", value: iceClass.edOpenWater, unit: "" },
		{ label: "E voyages, ice conditions, adjusted", value: iceClass.eVoyagesIceAdjusted, unit: "GJ" },
		{ label: "E additional due to ice conditions", value: iceClass.eAdditionalDueIceConditions, unit: "GJ" },
		{ label: "E voyages, open water x 1.3", value: iceClass.eVoyagesOpenWaterX1, unit: "GJ" },
		{ label: "Ice Class criteria met?", value: iceClass.eVoyagesIceClassCriteriaMet ? "Yes" : "No", unit: "" },
		{ label: "Limitation criteria met?", value: iceClass.limitationCriteriaMet ? "Yes" : "No", unit: "" },
		{ label: "E additional due to ice class", value: iceClass.eAdditionalDueIceClass, unit: "GJ" },
		{ label: "Ice Class criteria met?", value: iceClass.eAdditionalIceClassCriteriaMet ? "Yes" : "No", unit: "" },
		{ label: "E additional ice", value: iceClass.eAdditionalIce, unit: "GJ" },
	];

	return (
		<Box>
			<Box>
					<div className="d-flex justify-content-between mt-4 mb-4">
						<Box>
						<Box sx={{ display: "flex", gap: 2, mb: 1 }}>
							<Box className="text">Total Energy in FEU scope (for allocation) :</Box>
							<Box
								sx={{
									minWidth: "150px",
									border: "1px solid rgb(204, 204, 204)",
									fontWeight: "normal",
									textAlign: "center",
									fontSize: "14.4px",
									padding: "5px 0px",
								}}
							>
								{roundOff(energySummary.totalEnergyFEUAllocation, 3, 3)}
							</Box>
							<Typography sx={{ ml: -1 }}>GJ</Typography>
						</Box>

						<Box sx={{ display: "flex", gap: 2, mb: 1,ml:3 }}>
							<Box className="text" sx={{ marginLeft: "-3px" }}>
								Total Energy in FEU scope (remaining) :
							</Box>
							<Box
								sx={{
									minWidth: "150px",
									border: "1px solid rgb(204, 204, 204)",
									fontWeight: "normal",
									textAlign: "center",
									fontSize: "14.4px",
									padding: "5px 0px",
								}}
							>
								{roundOff(energySummary.totalEnergyFEURemaining, 3, 3)}

							</Box>
							<Typography sx={{ ml: -1 }}>GJ</Typography>
							<Box >
								{energySummary.totalEnergyFEURemaining > 0 && (
									<Typography color="error" sx={{ ml: -1 }}>
										All remaining energy should be allocated
									</Typography>
								)}
							</Box>
						</Box>
					</Box>
					<Box>
						<Box sx={{ display: "flex", gap: 2, mb: 1}}>
							<Box className="text">E additional ice (for allocation) :</Box>
							<Box
								sx={{
									minWidth: "150px",
									border: "1px solid rgb(204, 204, 204)",
									fontWeight: "normal",
									textAlign: "center",
									fontSize: "14.4px",
									padding: "5px 0px",
								}}
							>
								{roundOff(energySummary.eAdditionalIceAllocation, 3, 3)}
							</Box>
							<Typography sx={{ ml: -1 }}>GJ</Typography>
						</Box>

						<Box sx={{ display: "flex", gap: 2, mb: 1,ml:3 }}>
							<Box className="text" sx={{ marginLeft: "-3px" }}>
								E additional ice (remaining) :
							</Box>
							<Box
								sx={{
									minWidth: "150px",
									border: "1px solid rgb(204, 204, 204)",
									fontWeight: "normal",
									textAlign: "center",
									fontSize: "14.4px",
									padding: "5px 0px",
								}}
							>
								{roundOff(energySummary.eAdditionalIceRemaining, 3, 3)}
							</Box>
							<Typography sx={{ ml: -1 }}>GJ</Typography>
							<Box >
								{energySummary.eAdditionalIceRemaining > 0 && (
									<Typography color="error" sx={{ ml: -1 }}>
										All remaining additional ice should be allocated
									</Typography>
								)}
							</Box>
						</Box>


					</Box>
					</div>
						<MaterialReactTable table={table2} />
			</Box>

			{/* Ice Class Section */}
			<Box sx={{ mt: 5 }}>
				{iceClassDetails.map((item, i) => {
					const isDecimal = !isNaN(parseFloat(item.value)) && isFinite(item.value);
					const roundedValue = isDecimal ? parseFloat(item.value).toFixed(3) : item.value;

					return (
						<MDBRow key={i} className="mt-2 ml-2 dcs-reporsummary-container align-items-center">
							<MDBCol className="col-3 ">
								<InputLabel className="text">{item.label}</InputLabel>
							</MDBCol>
							<MDBCol className="col-3 d-flex align-items-center">
								<Input title={isDecimal ? item.value : ""} value={roundedValue} readOnly className="report-summmery-input" autoFocus={false} />
								<span className="ml-2">{item.unit}</span>
							</MDBCol>
						</MDBRow>
					)
				})}
			</Box>
		</Box>
	);
};

export default FEUMRVFEUContribution;
